import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    IconArrowLeft, IconPrinter, IconLoader2, IconAlertTriangle,
    IconCar, IconMapPin, IconUsers, IconHistory, IconTrash,
} from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import { PageLoader } from '../../components/common/Loader';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import VehicleEmployeeSearchField from './components/VehicleEmployeeSearchField';
import SignatureField, { EMPTY_SIGNATURE } from '../../components/common/SignatureField';
import {
    useVehicleRequest, useUpdateVehicleRequest, useDeleteVehicleRequest,
    useSubmitVehicleRequest, useReviewVehicleRequest, useApproveVehicleRequest,
    useRejectVehicleRequest, useAssignVehicle, useDispatchVehicleTrip,
    useCompleteVehicleTrip, useCancelVehicleRequest, useOpenVehicleRequestPdf,
} from '../../hooks/useVehicleRequests';
import PassengerEditor, { passengersToPayload, passengersFromApi } from './components/PassengerEditor';
import LocationEditor, { locationsToPayload, locationsFromApi, emptyLocationRow } from './components/LocationEditor';
import DriverSearchField from './components/DriverSearchField';
import {
    statusLabel, statusBadgeClass, getAvailableActions, classifyWorkflowError,
    formatDate, formatDateTime, formatTime, withoutEmpty,
    VEH_ACT_PERMISSIONS, VEH_DISPATCH_PERMISSIONS,
} from './utils';

// ─── SHARED MODAL SHELL ──────────────────────────────────────────────────────

function ModalShell({ title, onClose, children, footer, width = 'max-w-[560px]' }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-15" onClick={onClose}>
            <div
                className={`bg-bg-dark rounded-lg shadow-light border border-border w-full ${width} max-h-[90vh] overflow-y-auto`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-20 border-b border-border">
                    <h5 className="text-[16px]/[22px] font-semibold text-center">{title}</h5>
                </div>
                <div className="p-20 flex flex-col gap-15">{children}</div>
                <div className="flex gap-10 p-20 border-t border-border">{footer}</div>
            </div>
        </div>
    );
}

function SubmitButton({ pending, label, className = 'btn btn-primary flex-1' }) {
    return (
        <button type="submit" className={className} disabled={pending}>
            {pending ? (
                <span className="flex items-center justify-center gap-5">
                    <IconLoader2 size={14} className="animate-spin" /> Working...
                </span>
            ) : label}
        </button>
    );
}

// ─── WORKFLOW MODALS ─────────────────────────────────────────────────────────

// Reviewer cannot be the requester; pre-filled after a rejection.
function SubmitModal({ request, orgId, onClose, onError }) {
    const submit = useSubmitVehicleRequest();
    const [reviewer, setReviewer] = useState(
        request.reviewer ? { id: request.reviewer, name: request.reviewer_name || 'Previous reviewer' } : null
    );
    const [comment, setComment] = useState('');
    const [signature, setSignature] = useState(EMPTY_SIGNATURE);
    const [errors, setErrors] = useState({});

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!reviewer) { setErrors({ reviewer_id: 'Please choose who should review this request.' }); return; }

        submit.mutate(
            { id: request.id, orgId, data: { reviewer_id: reviewer.id, comment, signature } },
            {
                onSuccess: () => { toast.success('Request submitted for review.'); onClose(); },
                onError: (err) => onError(err, setErrors, onClose),
            }
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalShell
                title="Submit for Review"
                onClose={onClose}
                footer={
                    <>
                        <button type="button" onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={submit.isPending}>Cancel</button>
                        <SubmitButton pending={submit.isPending} label="Submit" />
                    </>
                }
            >
                <VehicleEmployeeSearchField
                    label="Reviewer"
                    required
                    orgId={orgId}
                    value={reviewer}
                    onSelect={setReviewer}
                    onClear={() => setReviewer(null)}
                    excludeIds={[request.requester]}
                    error={errors.reviewer_id}
                />
                <div>
                    <label className="block text-sm font-medium mb-5">Comment</label>
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={2}
                        placeholder="Optional note for the reviewer"
                        className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
                    />
                </div>
                <SignatureField value={signature} onChange={setSignature} defaultName={request.requester_name} error={errors.signature} />
            </ModalShell>
        </form>
    );
}

// One call clears the reviewer's step and names the approver.
function ReviewModal({ request, orgId, onClose, onError }) {
    const review = useReviewVehicleRequest();
    const [action, setAction] = useState('approve');
    const [approver, setApprover] = useState(null);
    const [comment, setComment] = useState('');
    const [reason, setReason] = useState('');
    const [signature, setSignature] = useState(EMPTY_SIGNATURE);
    const [errors, setErrors] = useState({});

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors({});

        if (action === 'approve' && !approver) {
            setErrors({ approver_id: 'Choose who should approve this request next.' });
            return;
        }
        if (action === 'reject' && !reason.trim()) {
            setErrors({ reason: 'A reason is required when rejecting.' });
            return;
        }

        const data = action === 'approve'
            ? { action: 'approve', approver_id: approver.id, comment, signature }
            : { action: 'reject', reason: reason.trim() };

        review.mutate({ id: request.id, orgId, data }, {
            onSuccess: () => {
                toast.success(action === 'approve' ? 'Review complete — sent for approval.' : 'Request rejected.');
                onClose();
            },
            onError: (err) => onError(err, setErrors, onClose),
        });
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalShell
                title="Review Request"
                onClose={onClose}
                footer={
                    <>
                        <button type="button" onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={review.isPending}>Cancel</button>
                        <SubmitButton
                            pending={review.isPending}
                            label={action === 'approve' ? 'Approve & Send' : 'Reject'}
                            className={`flex-1 ${action === 'approve' ? 'btn btn-primary' : 'btn btn-danger'}`}
                        />
                    </>
                }
            >
                <div className="flex gap-15">
                    {['approve', 'reject'].map(option => (
                        <label key={option} className="flex items-center gap-5 text-sm cursor-pointer capitalize">
                            <input type="radio" name="review-action" checked={action === option} onChange={() => setAction(option)} />
                            {option}
                        </label>
                    ))}
                </div>

                {action === 'approve' ? (
                    <>
                        <VehicleEmployeeSearchField
                            label="Approver"
                            required
                            orgId={orgId}
                            value={approver}
                            onSelect={setApprover}
                            onClear={() => setApprover(null)}
                            excludeIds={[request.requester, request.reviewer]}
                            error={errors.approver_id}
                        />
                        <div>
                            <label className="block text-sm font-medium mb-5">Comment</label>
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                rows={2}
                                placeholder="Optional note for the approver"
                                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
                            />
                        </div>
                        <SignatureField value={signature} onChange={setSignature} defaultName={request.reviewer_name} error={errors.signature} />
                    </>
                ) : (
                    <div>
                        <label className="block text-sm font-medium mb-5">Reason <span className="text-danger">*</span></label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            placeholder="Why is this request being rejected?"
                            className={`w-full bg-bg-light border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none ${errors.reason ? 'border-danger' : 'border-border'}`}
                        />
                        {errors.reason && <p className="text-danger text-xs mt-5">{errors.reason}</p>}
                    </div>
                )}
            </ModalShell>
        </form>
    );
}

function ApproveModal({ request, orgId, onClose, onError }) {
    const approve = useApproveVehicleRequest();
    const [comment, setComment] = useState('');
    const [signature, setSignature] = useState(EMPTY_SIGNATURE);
    const [errors, setErrors] = useState({});

    const handleSubmit = (e) => {
        e.preventDefault();
        approve.mutate({ id: request.id, orgId, data: { comment, signature } }, {
            onSuccess: () => { toast.success('Request approved.'); onClose(); },
            onError: (err) => onError(err, setErrors, onClose),
        });
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalShell
                title="Approve Request"
                onClose={onClose}
                footer={
                    <>
                        <button type="button" onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={approve.isPending}>Cancel</button>
                        <SubmitButton pending={approve.isPending} label="Approve" />
                    </>
                }
            >
                <div>
                    <label className="block text-sm font-medium mb-5">Comment</label>
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={2}
                        className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
                    />
                </div>
                <SignatureField value={signature} onChange={setSignature} defaultName={request.approver_name} error={errors.signature} />
            </ModalShell>
        </form>
    );
}

// Reject from either stage, and cancel — both are a single required free-text reason.
function ReasonModal({ title, label, confirmLabel, danger, mutation, request, orgId, onClose, onError, required = true }) {
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState({});

    const handleSubmit = (e) => {
        e.preventDefault();
        if (required && !reason.trim()) { setErrors({ reason: 'This field is required.' }); return; }

        mutation.mutate({ id: request.id, orgId, data: { reason: reason.trim() } }, {
            onSuccess: () => { toast.success(`${confirmLabel} complete.`); onClose(); },
            onError: (err) => onError(err, setErrors, onClose),
        });
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalShell
                title={title}
                onClose={onClose}
                footer={
                    <>
                        <button type="button" onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={mutation.isPending}>Cancel</button>
                        <SubmitButton
                            pending={mutation.isPending}
                            label={confirmLabel}
                            className={`flex-1 ${danger ? 'btn btn-danger' : 'btn btn-primary'}`}
                        />
                    </>
                }
            >
                <div>
                    <label className="block text-sm font-medium mb-5">
                        {label} {required && <span className="text-danger">*</span>}
                    </label>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        className={`w-full bg-bg-light border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none ${errors.reason ? 'border-danger' : 'border-border'}`}
                    />
                    {errors.reason && <p className="text-danger text-xs mt-5">{errors.reason}</p>}
                </div>
            </ModalShell>
        </form>
    );
}

// Dispatch and complete take only an optional comment.
function CommentModal({ title, confirmLabel, mutation, request, orgId, onClose, onError }) {
    const [comment, setComment] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        // Nothing here can fail validation, so there are no field errors to show.
        mutation.mutate({ id: request.id, orgId, data: { comment } }, {
            onSuccess: () => { toast.success(`${confirmLabel} complete.`); onClose(); },
            onError: (err) => onError(err, null, onClose),
        });
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalShell
                title={title}
                onClose={onClose}
                footer={
                    <>
                        <button type="button" onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={mutation.isPending}>Cancel</button>
                        <SubmitButton pending={mutation.isPending} label={confirmLabel} />
                    </>
                }
            >
                <div>
                    <label className="block text-sm font-medium mb-5">Comment</label>
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={2}
                        className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
                    />
                </div>
            </ModalShell>
        </form>
    );
}

// Staff driver via picker (backend snapshots name/phone), or a hired driver typed in.
function AssignModal({ request, orgId, onClose, onError, onWarnings }) {
    const assign = useAssignVehicle();
    const [mode, setMode] = useState('staff');
    const [driver, setDriver] = useState(null);
    const [form, setForm] = useState({ driver_name: '', driver_phone: '', vehicle_reg_no: '', assigned_departure_time: '', remarks: '' });
    const [errors, setErrors] = useState({});

    const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors({});

        if (!form.vehicle_reg_no.trim()) { setErrors({ vehicle_reg_no: 'Vehicle registration number is required.' }); return; }
        if (mode === 'staff' && !driver) { setErrors({ driver_employee_id: 'Choose a staff driver, or switch to a hired driver.' }); return; }
        if (mode === 'hired' && !form.driver_name.trim()) { setErrors({ driver_name: 'Driver name is required for a hired driver.' }); return; }

        const data = {
            vehicle_reg_no: form.vehicle_reg_no.trim(),
            assigned_departure_time: form.assigned_departure_time || null,
            remarks: form.remarks.trim(),
            ...(mode === 'staff'
                ? { driver_employee_id: driver.id }
                : { driver_name: form.driver_name.trim(), driver_phone: form.driver_phone.trim() }),
        };

        assign.mutate({ id: request.id, orgId, data }, {
            onSuccess: (res) => {
                toast.success('Vehicle assigned.');
                // 200 can carry overlap warnings — shown as an amber notice, not an error.
                if (res?.warnings?.length) onWarnings(res.warnings);
                onClose();
            },
            onError: (err) => onError(err, setErrors, onClose),
        });
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalShell
                title="Assign Vehicle"
                onClose={onClose}
                footer={
                    <>
                        <button type="button" onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={assign.isPending}>Cancel</button>
                        <SubmitButton pending={assign.isPending} label="Assign" />
                    </>
                }
            >
                <div className="flex gap-15">
                    <label className="flex items-center gap-5 text-sm cursor-pointer">
                        <input type="radio" name="driver-mode" checked={mode === 'staff'} onChange={() => setMode('staff')} />
                        Staff driver
                    </label>
                    <label className="flex items-center gap-5 text-sm cursor-pointer">
                        <input type="radio" name="driver-mode" checked={mode === 'hired'} onChange={() => setMode('hired')} />
                        Hired driver
                    </label>
                </div>

                {mode === 'staff' ? (
                    <DriverSearchField
                        orgId={orgId}
                        value={driver}
                        onSelect={setDriver}
                        onClear={() => setDriver(null)}
                        error={errors.driver_employee_id}
                    />
                ) : (
                    <div className="grid md:grid-cols-2 grid-cols-1 gap-15">
                        <div>
                            <label className="block text-sm font-medium mb-5">Driver Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                value={form.driver_name}
                                onChange={(e) => setField('driver_name', e.target.value)}
                                className={`w-full bg-bg-light border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none ${errors.driver_name ? 'border-danger' : 'border-border'}`}
                            />
                            {errors.driver_name && <p className="text-danger text-xs mt-5">{errors.driver_name}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-5">Driver Phone</label>
                            <input
                                type="text"
                                value={form.driver_phone}
                                onChange={(e) => setField('driver_phone', e.target.value)}
                                placeholder="+234..."
                                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                            />
                        </div>
                    </div>
                )}

                <div className="grid md:grid-cols-2 grid-cols-1 gap-15">
                    <div>
                        <label className="block text-sm font-medium mb-5">Vehicle Reg. No <span className="text-danger">*</span></label>
                        <input
                            type="text"
                            value={form.vehicle_reg_no}
                            onChange={(e) => setField('vehicle_reg_no', e.target.value)}
                            placeholder="ABC-123-XY"
                            className={`w-full bg-bg-light border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none ${errors.vehicle_reg_no ? 'border-danger' : 'border-border'}`}
                        />
                        {errors.vehicle_reg_no && <p className="text-danger text-xs mt-5">{errors.vehicle_reg_no}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-5">Assigned Departure Time</label>
                        <input
                            type="time"
                            value={form.assigned_departure_time}
                            onChange={(e) => setField('assigned_departure_time', e.target.value)}
                            className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-5">Remarks</label>
                    <textarea
                        value={form.remarks}
                        onChange={(e) => setField('remarks', e.target.value)}
                        rows={2}
                        className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
                    />
                </div>
            </ModalShell>
        </form>
    );
}

// ─── READ-ONLY PANELS ────────────────────────────────────────────────────────

function InfoRow({ label, value }) {
    return (
        <div>
            <p className="text-xs text-text-secondary">{label}</p>
            <p className="text-sm mt-2">{value || '-'}</p>
        </div>
    );
}

function Timeline({ actions }) {
    if (!actions?.length) {
        return <p className="text-sm text-text-secondary">No activity yet.</p>;
    }
    return (
        <div className="flex flex-col gap-15">
            {actions.map((entry, index) => (
                <div key={entry.id || index} className="flex gap-10">
                    <div className="flex flex-col items-center">
                        <span className="w-[8px] h-[8px] rounded-full bg-primary mt-5" />
                        {index < actions.length - 1 && <span className="flex-1 w-[1px] bg-border mt-3" />}
                    </div>
                    <div className="flex-1 pb-5">
                        <p className="text-sm font-medium capitalize">
                            {String(entry.action || '').replace(/_/g, ' ')}
                            <span className="text-text-secondary font-normal"> — {entry.actor_name || 'Unknown'}</span>
                        </p>
                        <p className="text-xs text-text-secondary mt-2">{formatDateTime(entry.acted_at)}</p>
                        {entry.comment && <p className="text-sm mt-5">{entry.comment}</p>}
                        {entry.signature_label && (
                            <p className="text-xs text-text-secondary mt-5 italic">Signed: {entry.signature_label}</p>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─── PAGE ────────────────────────────────────────────────────────────────────

export default function VehicleRequestDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;

    const { data: request, isLoading, refetch } = useVehicleRequest(id, orgId);
    const updateRequest = useUpdateVehicleRequest();
    const deleteRequest = useDeleteVehicleRequest();
    const openPdf = useOpenVehicleRequestPdf();

    const rejectMutation = useRejectVehicleRequest();
    const cancelMutation = useCancelVehicleRequest();
    const dispatchMutation = useDispatchVehicleTrip();
    const completeMutation = useCompleteVehicleTrip();

    const [activeModal, setActiveModal] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [warnings, setWarnings] = useState([]);

    const canAct = hasAnyPermission(VEH_ACT_PERMISSIONS);
    const canDispatch = hasAnyPermission(VEH_DISPATCH_PERMISSIONS);
    const actions = useMemo(
        () => getAvailableActions(request, user, { canAct, canDispatch }),
        [request, user, canAct, canDispatch]
    );

    // Editable form state, hydrated from the payload whenever it changes.
    const [form, setForm] = useState({ purpose_of_trip: '', departure_date: '', departure_time: '', return_date: '', expected_return_time: '' });
    const [passengers, setPassengers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (!request) return;
        setForm({
            purpose_of_trip: request.purpose_of_trip || '',
            departure_date: request.departure_date || '',
            departure_time: (request.departure_time || '').slice(0, 5),
            return_date: request.return_date || '',
            expected_return_time: (request.expected_return_time || '').slice(0, 5),
        });
        setPassengers(passengersFromApi(request.passengers));
        setLocations(request.locations?.length ? locationsFromApi(request.locations) : [emptyLocationRow()]);
    }, [request]);

    // A 409 means someone else moved first — refetch and re-render, don't error.
    const handleTransitionError = (error, setErrors, closeModal) => {
        const { kind, fieldErrors: errors, message } = classifyWorkflowError(error);
        if (kind === 'stale' || kind === 'not_assignee') {
            toast(message, { icon: '🔄' });
            refetch();
            closeModal?.();
            return;
        }
        setErrors?.(errors);
        toast.error(message);
    };

    // is_editable covers status; actions.includes('edit') adds the requester/veh_act check.
    const isEditable = (request?.is_editable ?? ['draft', 'rejected'].includes(request?.status))
        && actions.includes('edit');

    const handleSave = () => {
        setFieldErrors({});
        const locationPayload = locationsToPayload(locations);
        if (locationPayload.length === 0) {
            setFieldErrors({ locations: 'Add at least one location with a state and address.' });
            return;
        }

        // Full replacement — these arrays become the record exactly as shown.
        const data = {
            ...withoutEmpty({
                purpose_of_trip: form.purpose_of_trip.trim(),
                departure_date: form.departure_date,
                departure_time: form.departure_time,
                return_date: form.return_date,
                expected_return_time: form.expected_return_time,
            }),
            locations: locationPayload,
            passengers: passengersToPayload(passengers),
        };

        updateRequest.mutate({ id, orgId, data }, {
            onSuccess: () => toast.success('Changes saved.'),
            onError: (err) => handleTransitionError(err, setFieldErrors),
        });
    };

    const handleDelete = () => {
        deleteRequest.mutate({ id, orgId }, {
            onSuccess: () => { toast.success('Draft deleted.'); navigate('/vehicle-requests'); },
            onError: (err) => { handleTransitionError(err); setConfirmDelete(false); },
        });
    };

    if (isLoading) return <PageLoader />;

    if (!request) {
        return (
            <div className="flex flex-col min-h-screen gap-20">
                <Breadcrumb title="Vehicle Request" path="Vehicle / Request" />
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 text-center text-text-secondary">
                    This request could not be loaded.
                </div>
            </div>
        );
    }

    const closeModal = () => setActiveModal(null);
    const assignment = request.assignment;

    return (
        <div className="flex flex-col min-h-screen gap-20">
            <Breadcrumb title={request.reference_no || 'Vehicle Request'} path="Vehicle / Request" />

            {/* Header */}
            <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                <div className="flex justify-between items-start flex-wrap gap-15">
                    <div className="flex items-start gap-10">
                        <button onClick={() => navigate(-1)} className="text-text-secondary hover:text-text-main mt-3">
                            <IconArrowLeft size={18} />
                        </button>
                        <div>
                            <div className="flex items-center gap-10 flex-wrap">
                                <h2 className="text-lg font-bold">{request.reference_no || 'Vehicle Request'}</h2>
                                <span className={`px-8 py-3 rounded-full text-xs font-medium ${statusBadgeClass(request.status)}`}>
                                    {statusLabel(request)}
                                </span>
                            </div>
                            <p className="text-sm text-text-secondary mt-5">
                                Raised by {request.requester_name || '-'} · {formatDate(request.created_at)}
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-10 flex-wrap">
                        <button
                            onClick={() => openPdf.mutate(
                                { id, orgId },
                                { onError: () => toast.error('Could not open the printed form.') }
                            )}
                            className="btn btn-outline-secondary flex items-center gap-5"
                            disabled={openPdf.isPending}
                        >
                            <IconPrinter size={16} /> Print
                        </button>

                        {actions.includes('submit') && (
                            <button onClick={() => setActiveModal('submit')} className="btn btn-primary">
                                {request.status === 'rejected' ? 'Resubmit' : 'Submit'}
                            </button>
                        )}
                        {actions.includes('review') && (
                            <button onClick={() => setActiveModal('review')} className="btn btn-primary">Review</button>
                        )}
                        {actions.includes('approve') && (
                            <button onClick={() => setActiveModal('approve')} className="btn btn-primary">Approve</button>
                        )}
                        {actions.includes('reject') && (
                            <button onClick={() => setActiveModal('reject')} className="btn btn-danger">Reject</button>
                        )}
                        {actions.includes('assign') && (
                            <button onClick={() => setActiveModal('assign')} className="btn btn-primary flex items-center gap-5">
                                <IconCar size={16} /> Assign Vehicle
                            </button>
                        )}
                        {actions.includes('dispatch_trip') && (
                            <button onClick={() => setActiveModal('dispatch')} className="btn btn-primary">Dispatch</button>
                        )}
                        {actions.includes('complete') && (
                            <button onClick={() => setActiveModal('complete')} className="btn btn-primary">Complete</button>
                        )}
                        {actions.includes('cancel') && (
                            <button onClick={() => setActiveModal('cancel')} className="btn btn-outline-secondary">Cancel Request</button>
                        )}
                        {actions.includes('delete') && (
                            <button onClick={() => setConfirmDelete(true)} className="btn btn-outline-secondary flex items-center gap-5">
                                <IconTrash size={16} />
                            </button>
                        )}
                    </div>
                </div>

                {request.status === 'rejected' && request.rejection_reason && (
                    <div className="mt-15 bg-danger/10 border border-danger/30 rounded-[4px] px-15 py-10">
                        <p className="text-sm text-danger font-medium">Rejected</p>
                        <p className="text-sm mt-3">{request.rejection_reason}</p>
                    </div>
                )}

                {warnings.length > 0 && (
                    <div className="mt-15 bg-amber-500/10 border border-amber-500/30 rounded-[4px] px-15 py-10">
                        <p className="text-sm text-amber-500 font-medium flex items-center gap-5">
                            <IconAlertTriangle size={16} /> Driver already booked on overlapping trips
                        </p>
                        <ul className="mt-5 text-sm">
                            {warnings.map((w, i) => (
                                <li key={i}>{w.reference_no} · {formatDate(w.from)} → {formatDate(w.to)}</li>
                            ))}
                        </ul>
                        <p className="text-xs text-text-secondary mt-5">
                            This assignment was still saved — review it if the clash matters.
                        </p>
                    </div>
                )}
            </div>

            <div className="grid xl:grid-cols-3 grid-cols-1 gap-20">
                <div className="xl:col-span-2 flex flex-col gap-20">
                    {/* Trip details */}
                    <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                        <h3 className="text-base font-semibold mb-15">Trip Details</h3>

                        {isEditable ? (
                            <div className="flex flex-col gap-15">
                                <div>
                                    <label className="block text-sm font-medium mb-5">
                                        Purpose of Trip <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        value={form.purpose_of_trip}
                                        onChange={(e) => setForm(prev => ({ ...prev, purpose_of_trip: e.target.value }))}
                                        rows={2}
                                        className={`w-full bg-bg-light border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none ${fieldErrors.purpose_of_trip ? 'border-danger' : 'border-border'}`}
                                    />
                                    {fieldErrors.purpose_of_trip && <p className="text-danger text-xs mt-5">{fieldErrors.purpose_of_trip}</p>}
                                </div>
                                <div className="grid md:grid-cols-2 grid-cols-1 gap-15">
                                    <div>
                                        <label className="block text-sm font-medium mb-5">Departure Date</label>
                                        <input type="date" value={form.departure_date} onChange={(e) => setForm(p => ({ ...p, departure_date: e.target.value }))}
                                            className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-5">Departure Time</label>
                                        <input type="time" value={form.departure_time} onChange={(e) => setForm(p => ({ ...p, departure_time: e.target.value }))}
                                            className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-5">Return Date</label>
                                        <input type="date" value={form.return_date} onChange={(e) => setForm(p => ({ ...p, return_date: e.target.value }))}
                                            className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-5">Expected Return Time</label>
                                        <input type="time" value={form.expected_return_time} onChange={(e) => setForm(p => ({ ...p, expected_return_time: e.target.value }))}
                                            className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none" />
                                    </div>
                                </div>

                                <LocationEditor value={locations} onChange={setLocations} error={fieldErrors.locations} />
                                <PassengerEditor value={passengers} onChange={setPassengers} orgId={orgId} error={fieldErrors.passengers} />

                                <div className="flex justify-end">
                                    <button onClick={handleSave} className="btn btn-primary" disabled={updateRequest.isPending}>
                                        {updateRequest.isPending ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-15">
                                <InfoRow label="Purpose of Trip" value={request.purpose_of_trip} />
                                <div className="grid md:grid-cols-4 grid-cols-2 gap-15">
                                    <InfoRow label="Departure Date" value={formatDate(request.departure_date)} />
                                    <InfoRow label="Departure Time" value={formatTime(request.departure_time)} />
                                    <InfoRow label="Return Date" value={formatDate(request.return_date)} />
                                    <InfoRow label="Expected Return" value={formatTime(request.expected_return_time)} />
                                </div>

                                <div>
                                    <p className="text-xs text-text-secondary flex items-center gap-5 mb-8">
                                        <IconMapPin size={14} /> Locations
                                    </p>
                                    {request.locations?.length ? (
                                        <div className="flex flex-col gap-8">
                                            {request.locations.map((loc, i) => (
                                                <div key={loc.id || i} className="border border-border rounded-[4px] px-10 py-8">
                                                    <p className="text-sm font-medium">{loc.full_address}</p>
                                                    <p className="text-xs text-text-secondary mt-2">
                                                        {[loc.state_name || loc.state, loc.purpose].filter(Boolean).join(' · ')}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : <p className="text-sm text-text-secondary">No locations recorded.</p>}
                                </div>

                                <div>
                                    <p className="text-xs text-text-secondary flex items-center gap-5 mb-8">
                                        <IconUsers size={14} /> Passengers
                                    </p>
                                    {request.passengers?.length ? (
                                        <div className="flex flex-col gap-8">
                                            {request.passengers.map((p, i) => (
                                                <div key={p.id || i} className="border border-border rounded-[4px] px-10 py-8">
                                                    <p className="text-sm font-medium">{p.name}</p>
                                                    <p className="text-xs text-text-secondary mt-2">
                                                        {[p.designation, p.organization].filter(Boolean).join(' · ')}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : <p className="text-sm text-text-secondary">No passengers recorded.</p>}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Assignment */}
                    {assignment && (
                        <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                            <h3 className="text-base font-semibold mb-15 flex items-center gap-5">
                                <IconCar size={18} /> Vehicle Assignment
                            </h3>
                            <div className="grid md:grid-cols-3 grid-cols-2 gap-15">
                                <InfoRow label="Vehicle Reg. No" value={assignment.vehicle_reg_no} />
                                <InfoRow label="Driver" value={assignment.driver_name} />
                                <InfoRow label="Driver Phone" value={assignment.driver_phone} />
                                <InfoRow label="Assigned Departure" value={formatTime(assignment.assigned_departure_time)} />
                                <InfoRow label="Remarks" value={assignment.remarks} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Side column */}
                <div className="flex flex-col gap-20">
                    <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                        <h3 className="text-base font-semibold mb-15">Workflow</h3>
                        <div className="flex flex-col gap-15">
                            <InfoRow label="Requester" value={request.requester_name} />
                            <InfoRow label="Reviewer" value={request.reviewer_name} />
                            <InfoRow label="Approver" value={request.approver_name} />
                        </div>
                    </div>

                    <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                        <h3 className="text-base font-semibold mb-15 flex items-center gap-5">
                            <IconHistory size={18} /> Activity
                        </h3>
                        <Timeline actions={request.actions} />
                    </div>
                </div>
            </div>

            {/* Modals */}
            {activeModal === 'submit' && (
                <SubmitModal request={request} orgId={orgId} onClose={closeModal} onError={handleTransitionError} />
            )}
            {activeModal === 'review' && (
                <ReviewModal request={request} orgId={orgId} onClose={closeModal} onError={handleTransitionError} />
            )}
            {activeModal === 'approve' && (
                <ApproveModal request={request} orgId={orgId} onClose={closeModal} onError={handleTransitionError} />
            )}
            {activeModal === 'reject' && (
                <ReasonModal
                    title="Reject Request" label="Reason" confirmLabel="Reject" danger
                    mutation={rejectMutation} request={request} orgId={orgId}
                    onClose={closeModal} onError={handleTransitionError}
                />
            )}
            {activeModal === 'cancel' && (
                <ReasonModal
                    title="Cancel Request" label="Reason" confirmLabel="Cancel Request" danger required={false}
                    mutation={cancelMutation} request={request} orgId={orgId}
                    onClose={closeModal} onError={handleTransitionError}
                />
            )}
            {activeModal === 'dispatch' && (
                <CommentModal
                    title="Dispatch Trip" confirmLabel="Dispatch"
                    mutation={dispatchMutation} request={request} orgId={orgId}
                    onClose={closeModal} onError={handleTransitionError}
                />
            )}
            {activeModal === 'complete' && (
                <CommentModal
                    title="Complete Trip" confirmLabel="Complete"
                    mutation={completeMutation} request={request} orgId={orgId}
                    onClose={closeModal} onError={handleTransitionError}
                />
            )}
            {activeModal === 'assign' && (
                <AssignModal
                    request={request} orgId={orgId} onClose={closeModal}
                    onError={handleTransitionError} onWarnings={setWarnings}
                />
            )}

            <ConfirmDeleteModal
                isOpen={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                onConfirm={handleDelete}
                title="Delete draft request"
                message="This draft will be permanently deleted. This action cannot be undone."
                confirmLabel="Delete Draft"
                isLoading={deleteRequest.isPending}
            />
        </div>
    );
}
